import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, FIELD_CLASS, Spinner } from '@/components/ui';
import { useLogin, useMe } from './useAuth';
import { loginErrorMessage, shouldOfferRetry } from './loginError';

/**
 * 이 시간이 지나도 응답이 없으면 문구를 바꾼다. (00-architecture.md 1.1절)
 *
 * 홈서버 미니PC 라 콜드 스타트가 있다. 첫 로그인은 세션·Redis·DB 가
 * 한꺼번에 깨어나는 순간이라 눈에 띄게 느릴 수 있다.
 * "고장났나" 를 묻기 전에 무슨 일이 일어나고 있는지 알려준다.
 */
const SLOW_THRESHOLD_MS = 3000;

/**
 * 로그인 화면.
 *
 * 가입 페이지가 없다. 계정은 관리자가 직접 만든다. (00-architecture.md 1절)
 * 비밀번호 찾기도 없다 — 사용자가 3~4명이고 전부 서로 아는 사이라,
 * 관리자 화면에서 초기화해 주는 편이 훨씬 빠르다.
 */
export function LoginPage() {
  const location = useLocation();
  const { me, isLoading } = useMe();
  const loginMutation = useLogin();

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  // 요청이 오래 끌고 있는가. 문구만 바꾸고 동작은 그대로 둔다.
  const [slow, setSlow] = useState(false);

  const pending = loginMutation.isPending;

  useEffect(() => {
    if (!pending) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), SLOW_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  // 세션 확인이 끝나기 전에 폼을 보여주면, 이미 로그인된 사용자에게
  // 로그인 화면이 한 번 깜빡였다가 사라진다.
  if (isLoading) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Spinner label="확인 중" />
      </main>
    );
  }

  if (me) {
    // 원래 가려던 곳이 있으면 그리로 돌려보낸다. (ProtectedRoute 가 state 에 넣어준다)
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    loginMutation.mutate({ loginId: loginId.trim(), password, rememberMe });
  };

  /*
    무엇을 보여줄지는 loginError.ts 가 정한다.

    🔴 여기서 판정하다가 2026-08-17 에 버그가 났다.
      isError 를 안 보고 error?.code 만 봤더니, 아무 일도 없는 첫 화면에서
      code 가 undefined 라는 이유로 "서버에 연결하지 못했습니다" 가 떴다.
      로그인은 정상으로 되는데 문구만 거짓말을 하고 있었다.
      화면상으로는 우리가 쓴 문구 그대로라 눈으로 잡히지 않는다.
  */
  const errorState = {
    isError: loginMutation.isError,
    code: loginMutation.error?.code,
    message: loginMutation.error?.message,
  };
  const errorMessage = loginErrorMessage(errorState);
  const connectionFailed = shouldOfferRetry(errorState);

  const retry = () => {
    loginMutation.mutate({ loginId: loginId.trim(), password, rememberMe });
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-muted p-4">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-fg">파크302호 개미들</h1>
          <p className="mt-1 text-xs text-fg-subtle">개인 자산 관리 대시보드</p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-line bg-surface p-5 shadow-sm"
        >
          <label className="block text-xs font-medium text-fg-muted" htmlFor="loginId">
            아이디
          </label>
          <input
            id="loginId"
            name="loginId"
            // 비밀번호 관리자가 아이디 칸을 알아보게 한다.
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            value={loginId}
            onChange={(event) => setLoginId(event.target.value)}
            // 요청 중에는 입력을 막는다. 두 번 제출되는 것과
            // 보낸 값과 화면의 값이 어긋나는 것을 함께 막는다.
            disabled={pending}
            className={`mt-1 ${FIELD_CLASS}`}
          />

          <label className="mt-4 block text-xs font-medium text-fg-muted" htmlFor="password">
            비밀번호
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={pending}
            className={`mt-1 ${FIELD_CLASS}`}
          />

          {/*
            로그인 유지.

            체크하면 30일짜리 영속 쿠키와 30일 세션 TTL 을 받는다.
            체크하지 않으면 세션 쿠키라 브라우저를 닫으면 사라진다.
            가족이 각자 폰에서 쓰므로 기본값을 켜둔다.

            공용 PC 를 쓸 일이 생기면 이 기본값을 끄는 것을 다시 생각해야 한다.
          */}
          <label className="mt-4 flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              disabled={pending}
              className="h-4 w-4"
            />
            로그인 유지
          </label>

          {/*
            오류는 모달을 띄우지 않고 버튼 위에 붙인다.
            화면에 요소가 몇 개 없는 곳에서 모달은 과하고,
            눈이 이미 가 있는 자리에 두는 편이 빨리 읽힌다.

            role="alert" 는 스크린리더가 이 문장을 즉시 읽게 한다.
            시각적으로는 나타나지만 소리로는 아무 일도 안 일어나는 것을 막는다.
          */}
          {errorMessage && !pending && (
            <div className="mt-4" role="alert">
              <p className="flex items-start gap-1.5 text-sm text-danger">
                <span aria-hidden="true">⚠</span>
                <span>{errorMessage}</span>
              </p>

              {connectionFailed && (
                <Button
                  variant="secondary"
                  fullWidth
                  className="mt-3"
                  onClick={retry}
                  disabled={!loginId.trim() || !password}
                >
                  다시 시도
                </Button>
              )}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            className="mt-5"
            disabled={pending || !loginId.trim() || !password}
          >
            {pending ? (
              <span className="flex items-center gap-2">
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden="true"
                />
                {/* 3초가 지나면 무슨 일이 일어나는지 말해준다.
                    미니PC 콜드 스타트에서 "고장났나" 를 묻지 않게 한다. */}
                {slow ? '서버를 깨우는 중입니다…' : '확인 중…'}
              </span>
            ) : (
              '로그인'
            )}
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-fg-subtle">
          계정은 관리자가 만듭니다.
        </p>
      </div>
    </main>
  );
}
